import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

/* Husky Custom Code - Start */
import AuthAwareButton from "@husky-hrc/Components/AuthAwareButton";
import ExternalAuthButton from "@husky-hrc/Components/ExternalAuthButton";
import { huskyThemedButtonProps } from "@husky-hrc/Utilities/Mixins/buttons";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { mapLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, History, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { connect } from "react-redux";
/* Husky Custom Code - End */

const enum fields {
    background = "background",
    image = "image",
    focalPoint = "focalPoint",
    backgroundColor = "backgroundColor",
    heading = "heading",
    minimumHeight = "minimumHeight",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "variant",
    bannerWidth = "bannerWidth",
    imageOverlay = "imageOverlay",
    partialOverlay = "partialOverlay",
    partialOverlayPositioning = "partialOverlayPositioning",
    disableButton = "disableButton",
    contentPadding = "contentPadding",
    centerTextVertically = "centerTextVertically",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    customCSS = "customCSS",
    // Husky Custom Code
    disableSecondaryButton = "disableSecondaryButton", // Husky Custom Code
    buttons = "buttons",
    loggedInHeading = "loggedInHeading",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.minimumHeight]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.imageOverlay]: string;
        [fields.partialOverlay]: boolean;
        [fields.partialOverlayPositioning]: "top" | "middle" | "bottom";
        [fields.disableButton]: boolean;
        [fields.contentPadding]: number;
        [fields.centerTextVertically]: boolean;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.customCSS]: string;
        // Husky Custom Code
        [fields.loggedInHeading]: string;
        [fields.disableSecondaryButton]: boolean;
        [fields.buttons]: ButtonModel[];
    };
    extendedStyles?: BannerStyles;
}

/* Husky Custom Code - Start */
interface ButtonModel {
    fields: {
        huskyButtonType: "normal" | "externalAuthProvider" | "authAware";
        title: string;
        loggedInTitle: string;
        loggedOutTitle: string;
        destination: LinkFieldValue;
        destinationLoggedIn: LinkFieldValue;
        destinationLoggedOut: LinkFieldValue;
        variant: "primary" | "secondary" | "tertiary";
        openInNewWindow: boolean;
        overriddenTitle: string;
    };
}

interface OptionalFieldReturn {
    label: string;
    loggedInTitle: string;
    loggedOutTitle: string;
    destination: LinkFieldValue;
    destinationLoggedIn: LinkFieldValue;
    destinationLoggedOut: LinkFieldValue;
    variant: "primary" | "secondary" | "tertiary";
    huskyButtonType: "normal" | "externalAuthProvider" | "authAware";
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const userName = state.context.session?.userName;
    const firstName = state.context.session?.firstName;

    const links = mapLinks<ButtonModel, OptionalFieldReturn>(
        state,
        ownProps.fields.buttons,
        (widgetLink, stateLink) => ({
            label: widgetLink.fields.title ?? stateLink?.title,
            loggedInTitle: widgetLink.fields.loggedInTitle,
            loggedOutTitle: widgetLink.fields.loggedOutTitle,
            variant: widgetLink.fields.variant,
            destination: widgetLink.fields.destination,
            destinationLoggedOut: widgetLink.fields.destinationLoggedOut,
            destinationLoggedIn: widgetLink.fields.destinationLoggedIn,
            huskyButtonType: widgetLink.fields.huskyButtonType,
        }),
    );
    return {
        links,
        userName,
        firstName,
    };
};
/* Husky Custom Code - End */

export interface BannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    /**
     * @deprecated Use the `bannerButton` property instead.
     */
    slideCenteringWrapperStyles?: InjectableCss;
    bannerLink?: LinkPresentationProps;
    bannerButton?: ButtonPresentationProps;
    // Husky Custom Code
    bannerButtonMobile?: ButtonPresentationProps;
    bannerContentWrapper?: InjectableCss;
    bannerPrimaryContent?: InjectableCss;
    headerWrapper?: InjectableCss;
    headingStyles?: TypographyPresentationProps;
    subHeadingStyles?: TypographyPresentationProps;
    slantedBox?: InjectableCss;
}

export const bannerStyles: BannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    overlayWrapper: {
        css: css`
            display: flex;
            margin: 0 auto;
            color: white;
            text-align: left;
            width: 100%;
        `,
    },
    slantedBox: {
        css: css`
            position: relative;
            z-index: 1;

            &::after {
                background-color: rgba(255, 199, 44, 0.9);
                z-index: -1;
                content: "";
                display: block;
                position: absolute;
            }

            button {
                margin-top: 10px;
                margin-bottom: 10px;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100%;
                            padding: 6rem 2rem 4rem 2rem;
                            top: -56px;
                            margin-bottom: 56px;
                            width: 100%;
                            &::after {
                                border-bottom: 1rem solid #53565a;
                                transform: skewY(8deg);
                                right: 0;
                                top: 0;
                                bottom: 0;
                                width: 100%;
                                height: 110%;
                            }
                        `,
                        null,
                        null,
                        css`
                            overflow: hidden;
                            padding: 56px 300px 56px 100px;
                            top: 0;
                            margin-bottom: 0;
                            width: auto;

                            &::after {
                                border-bottom: none;
                                border-right: 1.875rem solid #53565a;
                                transform: skewX(-10deg);
                                right: 35%;
                                top: 0;
                                bottom: 0;
                                width: 200%;
                                height: 100%;
                            }
                        `,
                        css`
                            padding: 56px 300px 56px 235px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    bannerPrimaryContent: {
        css: css`
            max-width: 1640px;
            > div {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 75%;
                            `,
                            null,
                            css`
                                width: 60%;
                            `,
                        ],
                        "min",
                    )}
            }
        `,
    },
    bannerButton: huskyThemedButtonProps("secondary", "slanted"),
    headingStyles: {
        variant: "h1",
        css: css`
            color: #000000;
            margin-bottom: 20px;
            outline: none;
        `,
    },
    subHeadingStyles: {
        css: css`
            color: #000000;
            font-size: 1rem;
            font-weight: 400;
            margin-bottom: 20px;
        `,
        forwardAs: "p",
    },
};

const onClick = (history: History, link: string | undefined) => {
    if (link) {
        history.push(link);
    }
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory;

const TitleWrapper = getStyledWrapper("div");

export const Banner: FC<Props> = ({ fields, links, history, extendedStyles, id, userName, firstName }) => {
    const backgroundStyles =
        fields.background === "image"
            ? `background-image: url(${fields.image});
           background-size: cover;`
            : `background-color: ${fields.backgroundColor};`;

    const focalPointStyles = getFocalPointStyles(fields.focalPoint);

    let minimumHeightStyles;
    switch (fields.minimumHeight) {
        case "1/4 viewport":
            minimumHeightStyles = "min-height: 30vh;";
            break;
        case "1/2 viewport":
            minimumHeightStyles = "min-height: 60vh;";
            break;
        case "3/4 viewport":
            minimumHeightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            minimumHeightStyles = "min-height: 100vh;";
            break;
    }

    let overlayPositioningStyles;
    if (!fields.partialOverlay) {
        overlayPositioningStyles = "align-items: stretch;";
    } else if (fields.partialOverlayPositioning === "top") {
        overlayPositioningStyles = "align-items: flex-start;";
    } else if (fields.partialOverlayPositioning === "middle") {
        overlayPositioningStyles = "align-items: center;";
    } else if (fields.partialOverlayPositioning === "bottom") {
        overlayPositioningStyles = "align-items: flex-end;";
    }

    const styles = useMergeStyles("banner", bannerStyles, extendedStyles);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${backgroundStyles}
            ${focalPointStyles}
            ${minimumHeightStyles}
            ${overlayPositioningStyles}
			display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            align-items: flex-start;
                        `,
                        null,
                        null,
                        css`
                            align-items: center;
                        `,
                    ],
                    "min",
                )}
        `,
    };

    const overlayWrapperStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            background-color: ${fields.background === "image" ? fields.imageOverlay : ""};
        `,
    };

    const buttonLinks = links.map((link, index) => {
        if (link.huskyButtonType === "externalAuthProvider" && !userName) {
            return (
                <ExternalAuthButton
                    key={index} // eslint-disable-line react/no-array-index-key
                    extendedStyles={styles.bannerButton}
                    variant={link.variant}
                    authButtonText={link.label || link.title || link.url}
                ></ExternalAuthButton>
            );
        }
        if (link.huskyButtonType === "authAware") {
            return (
                <AuthAwareButton
                    key={index} // eslint-disable-line react/no-array-index-key
                    labelIfLoggedOut={link.loggedOutTitle}
                    labelIfLoggedIn={link.loggedInTitle}
                    destination={link.destination}
                    destinationIfLoggedOut={link.destinationLoggedOut}
                    destinationIfLoggedIn={link.destinationLoggedIn}
                    extendedStyles={styles.bannerButton}
                ></AuthAwareButton>
            );
        }
        if (link.huskyButtonType === "normal") {
            return (
                <Button
                    key={index} // eslint-disable-line react/no-array-index-key
                    {...styles.bannerButton}
                    variant={link.variant}
                    onClick={() => onClick(history, link.url)}
                >
                    {translate(link.label || link.title || (link.url as string))}
                </Button>
            );
        }
        return null;
    });

    let heading = fields.heading;

    if (userName && firstName && fields.loggedInHeading) {
        heading = fields.loggedInHeading.replace("{firstName}", firstName);
    }

    return (
        <>
            <StyledWrapper {...customCssWrapper}>
                <StyledWrapper {...overlayWrapperStyles}>
                    <StyledWrapper {...styles.slantedBox}>
                        <StyledWrapper {...styles.bannerPrimaryContent}>
                            <TitleWrapper>
                                <Typography tabIndex={-1} data-id="PageTitle" {...styles.headingStyles}>
                                    {heading}
                                </Typography>
                            </TitleWrapper>
                            <Hidden below="lg">
                                <Typography {...styles.subHeadingStyles}>{fields.subheading}</Typography>
                            </Hidden>
                            {buttonLinks}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const defaultCustomCss = `.banner-wrapper {
}

.banner-overlay-wrapper {
}

.banner-center-wrapper {
}

.banner-heading {
}

.banner-subheading {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const banner: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(Banner)),
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.imageOverlay,
                displayName: "Image Color Overlay",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.partialOverlay,
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                fieldType: "General",
                name: fields.partialOverlayPositioning,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top", value: "top" },
                    { displayName: "Middle", value: "middle" },
                    { displayName: "Bottom", value: "bottom" },
                ],
                hideEmptyOption: true,
                defaultValue: "bottom",
                isVisible: widget => widget.fields.partialOverlay,
            },
            {
                fieldType: "General",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.background === "color",
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "TextField",
                displayName: "Heading",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.loggedInHeading,
                editorTemplate: "TextField",
                displayName: "Heading (Logged In)",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.subheading,
                editorTemplate: "TextField",
                displayName: "Subheading",
                defaultValue: "",
            },
            {
                name: fields.buttons,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "huskyButtonType",
                        displayName: "Button Type",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Normal", value: "normal" },
                            { displayName: "External Auth", value: "externalAuthProvider" },
                            { displayName: "Auth-Aware", value: "authAware" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "normal",
                        isRequired: true,
                    },
                    {
                        name: "title",
                        displayName: "Button Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "loggedInTitle",
                        displayName: "Title (Logged In)",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => item.fields.huskyButtonType === "authAware",
                    },
                    {
                        name: "loggedOutTitle",
                        displayName: "Title (Logged Out)",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => item.fields.huskyButtonType === "authAware",
                    },
                    {
                        name: "destination",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isRequired: true,
                        isVisible: item => item.fields.huskyButtonType !== "authAware",
                    },
                    {
                        name: "destinationLoggedOut",
                        displayName: "Button Link (Logged Out)",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isVisible: item => item.fields.huskyButtonType === "authAware",
                    },
                    {
                        name: "destinationLoggedIn",
                        displayName: "Button Link (Logged In)",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isVisible: item => item.fields.huskyButtonType === "authAware",
                    },
                    {
                        name: "variant",
                        displayName: "Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                    },
                ],
            },
            {
                fieldType: "General",
                name: fields.minimumHeight,
                displayName: "Minimum Banner Height",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default banner;
